// Versión UI (inputs controlados) de los campos de `pdfEncabezadoMsp.ts` —
// se renderiza arriba de cada uno de los 10 formularios del Anexo 1 MSP
// (010A/010B/012A/012B/013A/013B/118/122/054/055) y de 116-cert. Sexo/edad/
// fecha de nacimiento/N° HCU se leen del `Patient` (solo lectura, no se
// re-tipean acá — se editan en DatosDemograficosTab.tsx); institución/
// unicódigo/establecimiento/N° archivo/hoja son texto libre, con memoria de
// los últimos valores tipeados por campo (`encabezadoMspCache.ts`,
// `<datalist>` nativo del navegador — mismo patrón que
// `recetas/telefonosPrescriptorRecientes.ts`) — pedido explícito del usuario
// 2026-09-04. Unicódigo/establecimiento además arrancan PRECARGADOS con el
// último valor (no solo sugeridos): no cambian de un formulario a otro
// dentro del mismo consultorio.
import type { Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum, useResource } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { obtenerDatosConsultorio } from '../paciente/vaultClient';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { especialidadPorCodigo } from '@/lib/catalogoEspecialidades';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import { SISTEMA_HCU } from '../paciente/DatosDemograficosTab';
import { recientesDe, recordarCampoEncabezado, ultimoUnicodigoYEstablecimiento } from './encabezadoMspCache';
import { edadMsp, formatoEdadMsp } from '@logiacare/hce-pdf';

export interface CamposEncabezadoMsp {
  institucion: string;
  unicodigo: string;
  establecimiento: string;
  numeroArchivo: string;
  hoja: string;
}

// `hoja` ya no se captura: el PDF numera cada página como "Hoja n de N" (2026-09-18, ver `numerarHojasMsp` en hce-pdf).
const CAMPOS_TEXTO_LIBRE = ['institucion', 'unicodigo', 'establecimiento', 'numeroArchivo'] as const satisfies readonly (keyof CamposEncabezadoMsp)[];

/**
 * Valores iniciales para `useState` en cada formulario. `hoja` arranca en
 * "1" (texto libre, no autoincrementa). Unicódigo/establecimiento arrancan
 * vacíos acá a propósito — el `practitionerId` (para no mezclar la memoria
 * de un practicante con la de otro en un equipo compartido) todavía no está
 * disponible en este punto; `EncabezadoMspCampos` los precarga apenas monta,
 * ver su `useEffect`.
 */
export function valoresInicialesEncabezadoMsp(institucionSugerida = ''): CamposEncabezadoMsp {
  return { institucion: institucionSugerida, unicodigo: '', establecimiento: '', numeroArchivo: '', hoja: '1' };
}

export interface EncabezadoMspCamposProps {
  readonly subject: Reference<Patient>;
  readonly value: CamposEncabezadoMsp;
  readonly onChange: (value: CamposEncabezadoMsp) => void;
  /** HCU-116 (Certificado de Discapacidad): el Excel oficial solo trae columnas M/A para la edad, no H/D. */
  readonly soloMesesAnios?: boolean;
}

export function EncabezadoMspCampos({ subject, value, onChange, soloMesesAnios }: EncabezadoMspCamposProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const practitionerId = medplum.getProfile()?.id ?? '';
  const patient = useResource<Patient>(subject);
  const especialidadDelPracticante = useEspecialidadDelPracticante();

  // Precarga unicódigo/establecimiento con lo último que tipeó ESTE
  // practicante (encabezadoMspCache.ts) — `valoresInicialesEncabezadoMsp`
  // arranca en blanco porque el `practitionerId` no está disponible ahí
  // todavía. Una sola vez por montaje, y solo si el formulario los trae
  // vacíos (nunca pisa lo que el usuario ya haya tipeado).
  // `valueRef` guarda lo ÚLTIMO emitido (no solo lo renderizado): si la razón social llega en el mismo tick que
  // la precarga de unicódigo/establecimiento (respuesta ya cacheada), no debe pisarlos con un `value` viejo.
  const valueRef = useRef(value);
  valueRef.current = value;
  const emitir = (v: CamposEncabezadoMsp): void => {
    valueRef.current = v;
    onChange(v);
  };
  const yaPrecargado = useRef(false);
  useEffect(() => {
    if (yaPrecargado.current || !practitionerId) return;
    yaPrecargado.current = true;
    if (value.unicodigo || value.establecimiento) return;
    const { unicodigo, establecimiento } = ultimoUnicodigoYEstablecimiento(practitionerId);
    if (unicodigo || establecimiento) {
      emitir({ ...value, unicodigo: value.unicodigo || unicodigo, establecimiento: value.establecimiento || establecimiento });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practitionerId]);

  // "Institución" arranca con la razón social de "Datos del consultorio" (pedido de Sue, 2026-09-18) — solo si
  // el formulario la trae vacía, y sin pisar lo que la persona ya haya tipeado mientras llegaba la respuesta.
  useEffect(() => {
    if (value.institucion) return;
    let vigente = true;
    obtenerDatosConsultorio()
      .then(({ razonSocial }) => {
        if (vigente && razonSocial && !valueRef.current.institucion) emitir({ ...valueRef.current, institucion: razonSocial });
      })
      .catch(() => undefined);
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const idDatalist = (clave: keyof CamposEncabezadoMsp): string => `msp-recientes-${clave}`;

  const campo = (clave: keyof CamposEncabezadoMsp) => ({
    value: value[clave],
    list: idDatalist(clave),
    onChange: (e: { currentTarget: { value: string } }) => {
      onChange({ ...value, [clave]: e.currentTarget.value });
    },
    // Recordar recién al salir del campo (no en cada tecla) — evita ensuciar
    // la lista de "recientes" con cada estado intermedio de lo que se está
    // tipeando todavía.
    onBlur: () => recordarCampoEncabezado(practitionerId, clave, value[clave]),
  });

  const nombrePaciente = patient?.name?.[0] ? `${patient.name[0].given?.join(' ') ?? ''} ${patient.name[0].family ?? ''}`.trim() : '';
  const numeroHcu = patient?.identifier?.find((i) => i.system === SISTEMA_HCU)?.value ?? '';
  const edad = edadMsp(patient?.birthDate, { soloMesesAnios });
  const sexo = patient?.gender ? t(`fichaPaciente.genero.${patient.gender}`) : '';
  const nombreEspecialidad = especialidadDelPracticante.cargando
    ? ''
    : (especialidadDelPracticante.codigo && especialidadPorCodigo(especialidadDelPracticante.codigo)?.nombre) || '';

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border p-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.encabezadoMsp.titulo')}</h4>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 sm:col-span-6">
          <Label htmlFor="msp-institucion">{t('formularios.encabezadoMsp.institucion')}</Label>
          <Input id="msp-institucion" {...campo('institucion')} />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Label htmlFor="msp-unicodigo">{t('formularios.encabezadoMsp.unicodigo')}</Label>
          <Input id="msp-unicodigo" {...campo('unicodigo')} />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Label htmlFor="msp-establecimiento">{t('formularios.encabezadoMsp.establecimiento')}</Label>
          <Input id="msp-establecimiento" {...campo('establecimiento')} />
        </div>

        <div className="col-span-12 sm:col-span-4">
          <Label>{t('formularios.encabezadoMsp.paciente')}</Label>
          <Input value={nombrePaciente} disabled />
        </div>
        <div className="col-span-6 sm:col-span-2">
          <Label>{t('fichaPaciente.sexo')}</Label>
          <Input value={sexo} disabled />
        </div>
        <div className="col-span-6 sm:col-span-2">
          <Label>{t('formularios.encabezadoMsp.edad')}</Label>
          <Input value={edad ? formatoEdadMsp(edad) : ''} disabled />
        </div>
        <div className="col-span-6 sm:col-span-2">
          <Label>{t('fichaPaciente.demograficos.fechaNacimiento')}</Label>
          <Input value={patient?.birthDate ?? ''} disabled />
        </div>
        <div className="col-span-6 sm:col-span-2">
          <Label>{t('formularios.encabezadoMsp.numeroHcu')}</Label>
          <Input value={numeroHcu} disabled />
        </div>

        <div className="col-span-12 sm:col-span-6">
          <Label htmlFor="msp-archivo">{t('formularios.encabezadoMsp.numeroArchivo')}</Label>
          <Input id="msp-archivo" {...campo('numeroArchivo')} />
        </div>
        <div className="col-span-12 sm:col-span-6">
          <Label>{t('formularios.encabezadoMsp.especialidad')}</Label>
          <Input value={nombreEspecialidad} disabled />
        </div>
      </div>
      {/* Autocompletado nativo del navegador para los 5 campos de texto
          libre de arriba — el sistema recuerda lo último que tipeó el médico
          en cada uno (encabezadoMspCache.ts, localStorage) y lo ofrece acá,
          pedido explícito del usuario. `<datalist>` no renderiza nada visible
          (display:none), así que da igual dónde vive en el árbol. */}
      {CAMPOS_TEXTO_LIBRE.map((clave) => (
        <datalist key={clave} id={idDatalist(clave)}>
          {recientesDe(practitionerId, clave).map((valor) => (
            <option key={valor} value={valor} aria-label={valor} />
          ))}
        </datalist>
      ))}
    </div>
  );
}
